/**
 * ValidacionPanel — Panel de exámenes con ClasificacionAptitud visible.
 *
 * Requirements: M4.4.1, M4.4.2, M4.4.4, M4.15.3, Issue #99, Issue #100
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  FlaskConical,
  CalendarClock,
  RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { useApiError } from "@/hooks/use-api-error";
import { useOverrideManual, type AtencionDetalle } from "@/hooks/use-muestras";
import {
  getBadgeVariantClasificacion,
  getTextoClasificacion,
  esAptoParaToma,
} from "../utils/clasificacion-aptitud";
import { OverrideDialog } from "./override-dialog";
import { formatTiempoEstimado } from "../utils/tiempo-estimado";

interface ValidacionPanelProps {
  atencion: AtencionDetalle;
  onValidar: () => void;
  isValidating: boolean;
  onContinuar: () => void;
}

export function ValidacionPanel({
  atencion,
  onValidar,
  isValidating,
  onContinuar,
}: ValidacionPanelProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [overrideValidacionId, setOverrideValidacionId] = useState<number | null>(null);
  const [overrideOrdenExamenId, setOverrideOrdenExamenId] = useState<number | null>(null);

  const overrideMutation = useOverrideManual(atencion.id, overrideOrdenExamenId ?? 0, {
    onSuccess: () => {
      toast.success(t("muestras.validacion.override_aplicado"));
      setOverrideValidacionId(null);
      setOverrideOrdenExamenId(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const validaciones = atencion.validaciones ?? [];
  const todasAptas =
    validaciones.length > 0 &&
    validaciones.every((v) => esAptoParaToma(v.clasificacion) || v.override_aplicado);

  // Issue #100: detectar si la encuesta fue modificada después de la última validación
  const encuestaModificadaDespuesDeValidar = (() => {
    if (validaciones.length === 0) return false;
    if (!atencion.condicion_actual) return false;
    const ultimaValidacion = validaciones.reduce((latest, v) =>
      v.updated_at > latest.updated_at ? v : latest
    );
    return atencion.condicion_actual.updated_at > ultimaValidacion.updated_at;
  })();

  const ICONOS = {
    APTO: <CheckCircle className="h-4 w-4 text-green-600" />,
    APTO_CON_OBSERVACION: <AlertCircle className="h-4 w-4 text-yellow-600" />,
    NO_APTO: <XCircle className="h-4 w-4 text-red-600" />,
    PENDIENTE_POR_TIEMPO: <Clock className="h-4 w-4 text-gray-500" />,
  };

  return (
    <div className="space-y-4">
      {/* Aviso: encuesta modificada después de validar — Issue #100 */}
      {encuestaModificadaDespuesDeValidar && (
        <div className="flex items-start gap-2 rounded-md border border-yellow-400/50 bg-yellow-50 dark:bg-yellow-950/20 p-3 text-sm text-yellow-800 dark:text-yellow-300">
          <RefreshCw className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{t("muestras.validacion.encuesta_modificada_revalidar")}</span>
        </div>
      )}

      {/* Botón validar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {validaciones.length === 0
            ? t("muestras.validacion.sin_validaciones")
            : t("muestras.validacion.n_examenes", { count: validaciones.length })}
        </p>
        <Button variant="outline" size="sm" onClick={onValidar} disabled={isValidating}>
          {isValidating ? t("common.loading") : t("muestras.validacion.ejecutar")}
        </Button>
      </div>

      {/* Lista de validaciones */}
      {validaciones.length > 0 && (
        <div className="space-y-2">
          {validaciones.map((v) => (
            <Card key={v.id}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {ICONOS[v.clasificacion]}
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{v.examen_nombre}</p>
                    {v.motivo_incumplimiento && (
                      <p className="text-xs text-muted-foreground truncate">
                        {v.motivo_incumplimiento}
                      </p>
                    )}
                    {/* Tiempo estimado de entrega — Issue #99 */}
                    {((v.dias_para_resultado ?? 0) > 0 || (v.horas_para_resultado ?? 0) > 0) && (
                      <p className="text-xs text-blue-600 flex items-center gap-1 mt-0.5">
                        <CalendarClock className="h-3 w-3 shrink-0" />
                        {t("muestras.validacion.tiempo_estimado")}:{" "}
                        {formatTiempoEstimado(v.dias_para_resultado, v.horas_para_resultado, t)}
                      </p>
                    )}
                    {v.override_aplicado && (
                      <p className="text-xs text-blue-600">
                        {t("muestras.validacion.override_por")}: {v.override_por_nombre}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={getBadgeVariantClasificacion(v.clasificacion)}>
                    {getTextoClasificacion(v.clasificacion)}
                  </Badge>
                  {v.clasificacion === "NO_APTO" && !v.override_aplicado && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setOverrideValidacionId(v.id);
                        setOverrideOrdenExamenId(v.orden_examen);
                      }}
                    >
                      {t("muestras.validacion.override")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Continuar */}
      {validaciones.length > 0 && (
        <div className="flex items-center justify-between pt-2 border-t">
          {!todasAptas && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <XCircle className="h-4 w-4 text-destructive shrink-0" />
              {t("muestras.validacion.hay_no_aptos")}
            </p>
          )}
          {todasAptas && (
            <p className="text-sm text-green-600 flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 shrink-0" />
              {t("muestras.validacion.todos_aptos")}
            </p>
          )}
          <Button onClick={onContinuar} disabled={!todasAptas} className="ml-auto">
            <FlaskConical className="h-4 w-4 mr-1.5" />
            {t("muestras.validacion.continuar_muestras")}
          </Button>
        </div>
      )}

      {/* Override dialog */}
      {overrideValidacionId && overrideOrdenExamenId && (
        <OverrideDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setOverrideValidacionId(null);
              setOverrideOrdenExamenId(null);
            }
          }}
          onConfirm={(justificacion) => overrideMutation.mutate({ justificacion })}
          isLoading={overrideMutation.isPending}
        />
      )}
    </div>
  );
}
